/*
 * `WidgetHost` — die eine Ebene, auf der die Datenquelle aufgelöst wird.
 *
 * Warum eine Komponente und nicht ein Hook in `renderWidget`: `renderWidget`
 * ist eine gewöhnliche Funktion, die typischerweise in einem `.map()` über
 * eine Widget-Liste aufgerufen wird. Ein `useContext` darin liefe in der
 * Renderphase der ELTERN-Komponente, und die Hook-Reihenfolge dieser
 * Komponente würde von der Länge der Liste abhängen — genau der Fall, den
 * die Hook-Regeln verbieten. Als eigene Komponente hat jedes Widget seinen
 * eigenen Hook-Slot.
 *
 * Vorrangregel: Prop vor Context. Damit bleibt der Simulator-Weg (Quelle
 * explizit mitgeben) unverändert möglich, während produktive Seiten nichts
 * mehr durchreichen müssen.
 */

import type { WidgetDefinition } from './widgets';
import type { IPointBindingSource } from './binding';
import { useOptionalPointBindingSource } from './PointBindingProvider';
import { renderWidget } from './widgets/index';
import { LookFrame, needsLookFrame } from './panel/LookFrame';
import { followedPoints } from './panel/followStore';

interface Props {
  widget: WidgetDefinition;
  /**
   * Explizite Quelle. Hat Vorrang vor dem Context. Ohne Angabe wird die
   * Quelle aus dem umgebenden Provider genommen.
   */
  source?: IPointBindingSource;
}

export function WidgetHost({ widget, source }: Props) {
  const fromContext = useOptionalPointBindingSource();
  const resolved = source ?? fromContext;

  if (!resolved) {
    // Sichtbar, nicht still. Ein leerer Platz an dieser Stelle wäre von
    // "Widget zeigt gerade nichts" nicht zu unterscheiden.
    return (
      <div class="hv-widget-fallback" role="alert"
           title="Kein <PointBindingSourceProvider> im Baum und keine source-Prop.">
        <strong>{widget.label ?? widget.kind}</strong>
        <span>Keine Datenquelle</span>
      </div>
    );
  }

  // Folgt das Widget einer Kachel mit abwechselnden Werten, gelten deren
  // Punkte fuer Skripte und Regeln (panel/followStore.ts).
  const points = followedPoints(widget.points, widget.follow);
  const effective = points === widget.points ? widget : { ...widget, points };
  const body = renderWidget({ widget: effective, source: resolved });
  return needsLookFrame(effective)
    ? <LookFrame widget={effective} source={resolved}>{body}</LookFrame>
    : <>{body}</>;
}
